import { useState, useMemo, useId, useEffect, useCallback } from 'react';
import { Loader2, AlertCircle, Play, Download, BarChart3, RefreshCw } from 'lucide-react';

function isoDateDaysAgo(days) {
  const d = new Date();
  d.setUTCDate(d.getUTCDate() - days);
  return d.toISOString().slice(0, 10);
}

function todayIsoUtc() {
  return new Date().toISOString().slice(0, 10);
}

function downloadCsv(filename, rows) {
  if (!rows.length) return;
  const headers = Object.keys(rows[0]);
  const esc = (v) => {
    const s = v == null ? '' : String(v);
    if (/[",\n]/.test(s)) return `"${s.replace(/"/g, '""')}"`;
    return s;
  };
  const lines = [headers.join(','), ...rows.map((r) => headers.map((h) => esc(r[h])).join(','))];
  const blob = new Blob([lines.join('\n')], { type: 'text/csv;charset=utf-8' });
  const a = document.createElement('a');
  a.href = URL.createObjectURL(blob);
  a.download = filename;
  a.click();
  URL.revokeObjectURL(a.href);
}

function flattenByPersonForCsv(byPerson) {
  const rows = [];
  for (const p of byPerson || []) {
    for (const t of p.byPayrollTimeType || []) {
      rows.push({
        personNumber: p.personNumber,
        totalHoursForPerson: p.totalHours,
        payrollTimeType: t.payrollTimeType,
        hours: t.hours,
      });
    }
  }
  return rows;
}

/** HCM ISO instants → dd-mm-yyyy date and 24h HH:mm:ss (UTC). */
function splitIsoDateTimeUtc(iso) {
  if (iso == null || String(iso).trim() === '') {
    return { date: '—', time: '—' };
  }
  const d = new Date(String(iso).trim());
  if (Number.isNaN(d.getTime())) {
    return { date: String(iso), time: '—' };
  }
  const dd = String(d.getUTCDate()).padStart(2, '0');
  const mm = String(d.getUTCMonth() + 1).padStart(2, '0');
  const yyyy = d.getUTCFullYear();
  const hh = String(d.getUTCHours()).padStart(2, '0');
  const mi = String(d.getUTCMinutes()).padStart(2, '0');
  const ss = String(d.getUTCSeconds()).padStart(2, '0');
  return { date: `${dd}-${mm}-${yyyy}`, time: `${hh}:${mi}:${ss}` };
}

/** Next UTC midnight strictly after `d` (start of the following calendar day in UTC). */
function nextUtcMidnightAfter(d) {
  return new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate() + 1, 0, 0, 0, 0));
}

/**
 * One logical time line → one row per UTC calendar day touched; hours = stop − start per segment (not Oracle measure).
 */
function expandDetailRowsByUtcDay(row) {
  const rawStart = row.startTime;
  const rawStop = row.stopTime;
  if (rawStart == null || String(rawStart).trim() === '' || rawStop == null || String(rawStop).trim() === '') {
    return [{ ...row, measure: null, _segmentIndex: 0 }];
  }
  const start = new Date(String(rawStart).trim());
  const stop = new Date(String(rawStop).trim());
  if (Number.isNaN(start.getTime()) || Number.isNaN(stop.getTime())) {
    return [{ ...row, measure: null, _segmentIndex: 0 }];
  }
  if (start >= stop) {
    return [{ ...row, measure: 0, _segmentIndex: 0 }];
  }
  const segments = [];
  let cursor = start;
  let idx = 0;
  while (cursor < stop) {
    const dayBreak = nextUtcMidnightAfter(cursor);
    const segmentEnd = stop < dayBreak ? stop : dayBreak;
    const ms = segmentEnd - cursor;
    const hours = ms / 3600000;
    segments.push({
      ...row,
      startTime: cursor.toISOString(),
      stopTime: segmentEnd.toISOString(),
      measure: hours,
      _segmentIndex: idx,
    });
    cursor = segmentEnd;
    idx += 1;
  }
  return segments.length ? segments : [{ ...row, measure: 0, _segmentIndex: 0 }];
}

export default function PayrollHoursByTimeTypeReport({
  api,
  user,
  connectionsList = [],
  selectedConnectionId,
  onSelectConnectionId,
  onRequestOracleLogin,
  /** (range from form) -> navigate to HCM time cards list */
  onOpenTimeCardList = null,
  /** (range from form) -> navigate to delete; prefill needs person for preview */
  onOpenHcmTimeCardDelete = null,
}) {
  const formId = useId();
  const hasCredentials = !!(user || (connectionsList && connectionsList.length > 0));

  const activeConnectionId = useMemo(() => {
    if (user) return null;
    if (!connectionsList?.length) return null;
    if (selectedConnectionId && connectionsList.some((c) => c.id === selectedConnectionId)) {
      return selectedConnectionId;
    }
    return connectionsList[0]?.id ?? null;
  }, [user, connectionsList, selectedConnectionId]);

  const [personNumber, setPersonNumber] = useState('');
  const [startDate, setStartDate] = useState(() => isoDateDaysAgo(13));
  const [endDate, setEndDate] = useState(todayIsoUtc);
  const [dataSource, setDataSource] = useState('timeRecords');
  const [groupType, setGroupType] = useState('TimeCardEntry');
  const [includeDetail, setIncludeDetail] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [result, setResult] = useState(null);
  const [reportJobId, setReportJobId] = useState(null);
  const [jobStatus, setJobStatus] = useState(null);

  const fetchJobStatus = useCallback(
    async (jobId) => {
      if (!jobId) return;
      try {
        const res = await api.get(`/api/jobs/${encodeURIComponent(jobId)}`, { timeout: 120000 });
        const data = res.data;
        setJobStatus(data);
        if (data.status === 'completed') {
          const rep = data.report;
          if (rep?.success) {
            setResult(rep);
            setError(null);
          } else {
            setResult(null);
            setError(rep?.error || data.message || 'Report finished without success payload.');
          }
          setReportJobId(null);
        } else if (data.status === 'failed') {
          setResult(null);
          setError(data.error || data.report?.error || 'Report job failed.');
          setReportJobId(null);
        }
      } catch (err) {
        setError((prev) => prev || err.response?.data?.error || err.message || 'Failed to refresh job status.');
      }
    },
    [api]
  );

  useEffect(() => {
    if (!reportJobId) return undefined;
    if (jobStatus?.status && ['completed', 'failed'].includes(jobStatus.status)) {
      return undefined;
    }
    const timer = window.setInterval(() => {
      fetchJobStatus(reportJobId);
    }, 5000);
    return () => window.clearInterval(timer);
  }, [reportJobId, jobStatus?.status, fetchJobStatus]);

  const runReport = async () => {
    setError(null);
    setResult(null);
    setReportJobId(null);
    setJobStatus(null);
    const pn = personNumber.trim();
    if (dataSource === 'timeRecords' && !pn) {
      setError('Person number is required when using Time records (finder).');
      return;
    }
    if (!hasCredentials) {
      setError('Connect to Oracle or add a saved connection first.');
      return;
    }
    setLoading(true);
    try {
      const body = {
        startDate,
        endDate,
        dataSource,
        detail: includeDetail,
      };
      if (pn) body.personNumber = pn;
      if (dataSource === 'timeRecords') {
        body.groupType = groupType.trim() || 'TimeCardEntry';
      }
      if (!user && activeConnectionId) {
        body.connectionId = activeConnectionId;
      }
      /** Queued jobs return 202 immediately; synchronous runs can exceed 3 minutes (e.g. bulk per-person Oracle calls). */
      const res = await api.post('/api/reports/payroll-hours-by-time-type', body, { timeout: 600000 });
      if (res.status === 202 && res.data?.jobId && res.data?.queued) {
        setReportJobId(res.data.jobId);
        setJobStatus(res.data);
        await fetchJobStatus(res.data.jobId);
        return;
      }
      setResult(res.data);
    } catch (err) {
      const msg =
        err.response?.data?.error ||
        err.message ||
        'Request failed.';
      setError(msg);
    } finally {
      setLoading(false);
    }
  };

  const reportJobPending = Boolean(reportJobId);

  const exportSummaryCsv = () => {
    if (!result?.summary?.length) return;
    const slug = result.personNumber || 'all-persons';
    const rows = result.summary.map((r) => ({
      payrollTimeType: r.payrollTimeType,
      hours: r.hours,
    }));
    downloadCsv(`payroll-hours-summary-${slug}-${result.startDate}_${result.endDate}.csv`, rows);
  };

  const exportByPersonCsv = () => {
    if (!result?.byPerson?.length) return;
    const slug = result.personNumber || 'all-persons';
    const flat = flattenByPersonForCsv(result.byPerson);
    if (!flat.length) return;
    downloadCsv(`payroll-hours-by-person-${slug}-${result.startDate}_${result.endDate}.csv`, flat);
  };

  const exportDetailCsv = () => {
    if (!result?.detail?.length) return;
    const slug = result.personNumber || 'all-persons';
    const expanded = result.detail.flatMap((r) => expandDetailRowsByUtcDay(r));
    const rows = expanded.map((r) => {
      const st = splitIsoDateTimeUtc(r.startTime);
      const sp = splitIsoDateTimeUtc(r.stopTime);
      return {
        personNumber: r.personNumber ?? '',
        payrollType: r.payrollType ?? '',
        absenceType: r.absenceType ?? '',
        absenceTypeId: r.absenceTypeId ?? '',
        hours: r.measure == null ? '' : r.measure,
        startDate: st.date,
        startTime: st.time,
        stopDate: sp.date,
        stopTime: sp.time,
        timeRecordId: r.timeRecordId ?? '',
        timeRecordGroupId: r.timeRecordGroupId ?? '',
      };
    });
    downloadCsv(`payroll-hours-detail-${slug}-${result.startDate}_${result.endDate}.csv`, rows);
  };

  const showByPerson =
    result?.byPerson?.length > 0 && (result.mode === 'allPersons' || result.byPerson.length > 1);

  const detailRowsExpanded = useMemo(() => {
    if (!result?.detail?.length) return [];
    return result.detail.flatMap((row) => expandDetailRowsByUtcDay(row));
  }, [result?.detail]);

  return (
    <div className="mx-auto max-w-5xl space-y-6">
      <div className="flex flex-wrap items-start gap-3">
        <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-gradient-to-br from-cyan-600 to-teal-600 text-white shadow-md">
          <BarChart3 className="h-5 w-5" />
        </div>
        <div className="min-w-0 flex-1">
          <h1 className="text-xl font-semibold text-slate-800">Payroll hours by time type</h1>
        </div>
      </div>

      {onOpenTimeCardList && (
        <div className="flex flex-col gap-2 rounded-xl border border-cyan-200/80 bg-cyan-50/60 px-4 py-3 text-sm text-slate-800 sm:flex-row sm:items-center sm:justify-between">
          <p className="min-w-0 leading-relaxed text-slate-700">
            <span className="font-semibold text-slate-900">HCM time cards</span> — this report is payroll hours / CSV.             To list raw <span className="font-mono text-xs">timeCards</span> for the same dates, or open delete, use the
            buttons. <span className="text-slate-800">Delete in HCM</span> needs a person in the form (preview is one
            person).
          </p>
          <div className="flex shrink-0 flex-wrap gap-2">
            <button
              type="button"
              onClick={() =>
                onOpenTimeCardList({ startDate, endDate, personNumber: personNumber.trim() || null })
              }
              className="inline-flex items-center gap-1.5 rounded-lg border border-cyan-300 bg-white px-3 py-2 text-xs font-medium text-cyan-900 shadow-sm hover:bg-cyan-100/60"
            >
              List in HCM
            </button>
            {onOpenHcmTimeCardDelete && (
              <button
                type="button"
                disabled={!personNumber.trim()}
                title={!personNumber.trim() ? 'Enter person number in the form to prefill delete (preview needs one person).' : undefined}
                onClick={() =>
                  onOpenHcmTimeCardDelete({ startDate, endDate, personNumber: personNumber.trim() || '' })
                }
                className="inline-flex items-center gap-1.5 rounded-lg border border-slate-300 bg-white px-3 py-2 text-xs font-medium text-slate-800 shadow-sm hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-50"
              >
                Delete in HCM
              </button>
            )}
          </div>
        </div>
      )}

      {!hasCredentials && (
        <div className="flex flex-col gap-3 rounded-xl border border-amber-200 bg-amber-50 p-4 text-sm text-amber-950 sm:flex-row sm:items-center sm:justify-between">
          <span className="flex items-center gap-2">
            <AlertCircle className="h-5 w-5 shrink-0 text-amber-700" />
            Sign in to Oracle or create a connection with HCM URL and credentials.
          </span>
          <button
            type="button"
            onClick={onRequestOracleLogin}
            className="shrink-0 rounded-lg bg-amber-700 px-4 py-2 text-sm font-medium text-white hover:bg-amber-800"
          >
            Oracle sign-in
          </button>
        </div>
      )}

      <div className="card-panel p-6 shadow-sm">
        <form
          id={formId}
          className="grid gap-4 sm:grid-cols-2"
          onSubmit={(e) => {
            e.preventDefault();
            runReport();
          }}
        >
          {!user && connectionsList.length > 0 && (
            <div className="sm:col-span-2">
              <label className="mb-1 block text-xs font-medium uppercase tracking-wide text-slate-500">
                Connection
              </label>
              {connectionsList.length === 1 ? (
                <p className="text-sm text-slate-700">
                  {connectionsList[0].connection_name || 'Saved connection'}
                </p>
              ) : (
                <select
                  value={activeConnectionId || ''}
                  onChange={(e) => onSelectConnectionId(e.target.value || null)}
                  className="w-full max-w-md rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm text-slate-800 shadow-sm focus:border-cyan-500 focus:outline-none focus:ring-1 focus:ring-cyan-500"
                >
                  {connectionsList.map((c) => (
                    <option key={c.id} value={c.id}>
                      {c.connection_name || c.application_hcm_url || 'Unnamed'}
                    </option>
                  ))}
                </select>
              )}
            </div>
          )}

          <div className="sm:col-span-2">
            <label htmlFor={`${formId}-src`} className="mb-1 block text-xs font-medium uppercase tracking-wide text-slate-500">
              Data source
            </label>
            <select
              id={`${formId}-src`}
              value={dataSource}
              onChange={(e) => setDataSource(e.target.value)}
              className="w-full max-w-md rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm text-slate-800 shadow-sm focus:border-cyan-500 focus:outline-none focus:ring-1 focus:ring-cyan-500"
            >
              <option value="timeCards">Time record groups + timeAttributes (payroll type and measure)</option>
              <option value="timeRecords">Time records (finder — one person only)</option>
            </select>
          </div>

          <div className="sm:col-span-2">
            <label htmlFor={`${formId}-pn`} className="mb-1 block text-xs font-medium uppercase tracking-wide text-slate-500">
              Person number (optional for time cards — all employees if blank)
            </label>
            <input
              id={`${formId}-pn`}
              type="text"
              value={personNumber}
              onChange={(e) => setPersonNumber(e.target.value)}
              className="w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm text-slate-800 shadow-sm focus:border-cyan-500 focus:outline-none focus:ring-1 focus:ring-cyan-500"
              placeholder={dataSource === 'timeCards' ? 'Leave empty for all employees (do not type BLANK)' : 'Required for time records'}
              autoComplete="off"
            />
          </div>

          {dataSource === 'timeRecords' && (
            <div className="sm:col-span-2">
              <label htmlFor={`${formId}-grp`} className="mb-1 block text-xs font-medium uppercase tracking-wide text-slate-500">
                HCM group (group type)
              </label>
              <input
                id={`${formId}-grp`}
                type="text"
                value={groupType}
                onChange={(e) => setGroupType(e.target.value)}
                className="w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm text-slate-800 shadow-sm focus:border-cyan-500 focus:outline-none focus:ring-1 focus:ring-cyan-500"
                placeholder="TimeCardEntry"
              />
            </div>
          )}

          <div>
            <label htmlFor={`${formId}-start`} className="mb-1 block text-xs font-medium uppercase tracking-wide text-slate-500">
              Start date
            </label>
            <input
              id={`${formId}-start`}
              type="date"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              className="w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm text-slate-800 shadow-sm focus:border-cyan-500 focus:outline-none focus:ring-1 focus:ring-cyan-500"
            />
          </div>

          <div>
            <label htmlFor={`${formId}-end`} className="mb-1 block text-xs font-medium uppercase tracking-wide text-slate-500">
              End date
            </label>
            <input
              id={`${formId}-end`}
              type="date"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              className="w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm text-slate-800 shadow-sm focus:border-cyan-500 focus:outline-none focus:ring-1 focus:ring-cyan-500"
            />
          </div>

          <div className="flex items-center gap-2 sm:col-span-2">
            <input
              id={`${formId}-detail`}
              type="checkbox"
              checked={includeDetail}
              onChange={(e) => setIncludeDetail(e.target.checked)}
              className="h-4 w-4 rounded border-slate-300 text-cyan-600 focus:ring-cyan-500"
            />
            <label htmlFor={`${formId}-detail`} className="text-sm text-slate-700">
              Include line-level rows
            </label>
          </div>

          <div className="sm:col-span-2">
            <button
              type="submit"
              disabled={loading || !hasCredentials || reportJobPending}
              className="inline-flex items-center justify-center gap-2 rounded-lg bg-gradient-to-r from-cyan-600 to-teal-600 px-4 py-2.5 text-sm font-medium text-white shadow-sm hover:from-cyan-700 hover:to-teal-700 disabled:cursor-not-allowed disabled:opacity-50"
            >
              {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Play className="h-4 w-4" />}
              Run report
            </button>
          </div>
        </form>
      </div>

      {reportJobId && (
        <div className="flex flex-col gap-3 rounded-xl border border-cyan-200 bg-cyan-50/80 p-4 text-sm text-slate-800 sm:flex-row sm:flex-wrap sm:items-center sm:justify-between">
          <div className="flex items-start gap-2 min-w-0">
            <Loader2 className="h-5 w-5 shrink-0 animate-spin text-cyan-700" />
            <div className="min-w-0">
              <p className="font-medium text-slate-900">Payroll report is running in the background</p>
              <p className="mt-1 text-xs text-slate-600">
                Job <span className="font-mono break-all">{reportJobId}</span>
                {jobStatus?.message ? (
                  <>
                    {' '}
                    · {jobStatus.message}
                  </>
                ) : null}
                {jobStatus?.progressPercent != null ? (
                  <>
                    {' '}
                    · {jobStatus.progressPercent}%
                  </>
                ) : null}
              </p>
            </div>
          </div>
          <button
            type="button"
            onClick={() => fetchJobStatus(reportJobId)}
            className="inline-flex shrink-0 items-center justify-center gap-2 rounded-lg border border-cyan-300 bg-white px-3 py-2 text-sm font-medium text-cyan-900 shadow-sm hover:bg-cyan-50"
          >
            <RefreshCw className="h-4 w-4" />
            Refresh status
          </button>
        </div>
      )}

      {error && (
        <div className="flex items-start gap-2 rounded-lg bg-red-50 p-4 text-sm text-red-800">
          <AlertCircle className="mt-0.5 h-5 w-5 shrink-0" />
          <span>{error}</span>
        </div>
      )}

      {result?.success && (
        <div className="card-panel overflow-hidden p-0 shadow-sm">
          <div className="flex flex-wrap items-center justify-between gap-3 border-b border-slate-100 bg-slate-50/80 px-4 py-3">
            <p className="text-sm text-slate-700">
              <span className="font-medium text-slate-900">{result.recordCount}</span> line{result.recordCount !== 1 ? 's' : ''}
              {result.timeCardCount != null && (
                <>
                  {' '}
                  · <span className="font-medium text-slate-900">{result.timeCardCount}</span> time card
                  {result.timeCardCount !== 1 ? 's' : ''}
                </>
              )}
              {result.detailRowCount != null && result.dataSource === 'timeCards' && (
                <>
                  {' '}
                  · <span className="font-medium text-slate-900">{result.detailRowCount}</span> child time record line
                  {result.detailRowCount !== 1 ? 's' : ''}
                </>
              )}
              {result.personNumber ? (
                <>
                  {' '}
                  · Person <span className="font-mono">{result.personNumber}</span>
                </>
              ) : (
                ' · All persons (date range)'
              )}
              {' · '}
              <span className="font-mono text-xs">{result.startDate}</span> →{' '}
              <span className="font-mono text-xs">{result.endDate}</span>
              {' · '}
              <span className="font-mono text-xs">{result.dataSource}</span>
            </p>
            <div className="flex flex-wrap gap-2">
              <button
                type="button"
                onClick={exportSummaryCsv}
                disabled={!result.summary?.length}
                className="inline-flex items-center gap-1.5 rounded-lg border border-slate-200 bg-white px-3 py-1.5 text-xs font-medium text-slate-700 hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-50"
              >
                <Download className="h-3.5 w-3.5" />
                CSV summary
              </button>
              {showByPerson && (
                <button
                  type="button"
                  onClick={exportByPersonCsv}
                  className="inline-flex items-center gap-1.5 rounded-lg border border-slate-200 bg-white px-3 py-1.5 text-xs font-medium text-slate-700 hover:bg-slate-50"
                >
                  <Download className="h-3.5 w-3.5" />
                  CSV by person
                </button>
              )}
            </div>
          </div>

          <div className="overflow-x-auto border-b border-slate-100 p-4">
            <h2 className="mb-2 text-xs font-semibold uppercase tracking-wide text-slate-500">Totals by payroll time type</h2>
            <table className="w-full min-w-[320px] border-collapse text-sm">
              <thead>
                <tr className="border-b border-slate-200 text-left text-xs font-semibold uppercase tracking-wide text-slate-500">
                  <th className="py-2 pr-4">Payroll time type</th>
                  <th className="py-2 text-right">Hours</th>
                </tr>
              </thead>
              <tbody>
                {result.summary.length === 0 ? (
                  <tr>
                    <td colSpan={2} className="py-8 text-center text-slate-500">
                      No rows in range. If types are still missing, try line detail or confirm your REST version exposes payroll fields on time card lines.
                    </td>
                  </tr>
                ) : (
                  result.summary.map((row) => (
                    <tr key={row.payrollTimeType} className="border-b border-slate-100 last:border-0">
                      <td className="py-2.5 pr-4 text-slate-800">{row.payrollTimeType}</td>
                      <td className="py-2.5 text-right font-mono text-slate-800 tabular-nums">
                        {Number(row.hours).toLocaleString(undefined, { maximumFractionDigits: 4 })}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {showByPerson && (
            <div className="max-h-96 overflow-auto p-4">
              <h2 className="mb-2 text-xs font-semibold uppercase tracking-wide text-slate-500">By person</h2>
              <table className="w-full min-w-[480px] border-collapse text-sm">
                <thead className="sticky top-0 bg-white">
                  <tr className="border-b border-slate-200 text-left text-xs font-semibold uppercase tracking-wide text-slate-500">
                    <th className="py-2 pr-4">Person number</th>
                    <th className="py-2 pr-4">Payroll time type</th>
                    <th className="py-2 text-right">Hours</th>
                    <th className="py-2 text-right">Person total</th>
                  </tr>
                </thead>
                <tbody>
                  {result.byPerson.map((p) =>
                    (p.byPayrollTimeType || []).map((t, i) => (
                      <tr key={`${p.personNumber}-${t.payrollTimeType}-${i}`} className="border-b border-slate-100">
                        <td className="py-2 pr-4 font-mono text-slate-800">{p.personNumber}</td>
                        <td className="py-2 pr-4 text-slate-800">{t.payrollTimeType}</td>
                        <td className="py-2 text-right font-mono tabular-nums">{Number(t.hours).toLocaleString(undefined, { maximumFractionDigits: 4 })}</td>
                        <td className="py-2 text-right font-mono text-slate-500 tabular-nums">
                          {i === 0 ? Number(p.totalHours).toLocaleString(undefined, { maximumFractionDigits: 4 }) : ''}
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          )}

          {detailRowsExpanded.length > 0 && (
            <div className="border-t border-slate-100 p-4">
              <div className="mb-3 flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
                <div>
                  <h2 className="text-sm font-semibold text-slate-800">Line detail</h2>
                </div>
                <button
                  type="button"
                  onClick={exportDetailCsv}
                  className="inline-flex shrink-0 items-center gap-1.5 rounded-lg border border-slate-200 bg-white px-3 py-1.5 text-xs font-medium text-slate-700 hover:bg-slate-50"
                >
                  <Download className="h-3.5 w-3.5" />
                  CSV detail
                </button>
              </div>
              <div className="max-h-80 overflow-auto rounded-lg border border-slate-100">
                <table className="w-full min-w-[960px] border-collapse text-xs">
                  <thead className="sticky top-0 bg-slate-50">
                    <tr className="text-left font-semibold uppercase tracking-wide text-slate-500">
                      <th className="px-2 py-2">Person</th>
                      <th className="px-2 py-2">Payroll type</th>
                      <th className="px-2 py-2">Absence type</th>
                      <th className="px-2 py-2 text-right">Hours</th>
                      <th className="px-2 py-2">Start date</th>
                      <th className="px-2 py-2">Start time</th>
                      <th className="px-2 py-2">Stop date</th>
                      <th className="px-2 py-2">Stop time</th>
                      <th className="px-2 py-2">Line / record id</th>
                    </tr>
                  </thead>
                  <tbody>
                    {detailRowsExpanded.map((row, i) => {
                      const payrollCol = row.payrollType != null && row.payrollType !== '' ? row.payrollType : '';
                      const absenceCol = row.absenceType != null && row.absenceType !== '' ? row.absenceType : '';
                      const startParts = splitIsoDateTimeUtc(row.startTime);
                      const stopParts = splitIsoDateTimeUtc(row.stopTime);
                      const hoursDisplay =
                        row.measure == null
                          ? '—'
                          : Number(row.measure).toLocaleString(undefined, { maximumFractionDigits: 4 });
                      return (
                        <tr
                          key={`${row.timeRecordId}-${row.timeRecordGroupId}-${i}-${row._segmentIndex ?? 0}`}
                          className="border-t border-slate-100"
                        >
                          <td className="px-2 py-2 font-mono text-slate-800">{row.personNumber ?? ''}</td>
                          <td className="px-2 py-2 text-slate-800">{payrollCol}</td>
                          <td className="px-2 py-2 text-slate-800">{absenceCol}</td>
                          <td className="px-2 py-2 text-right font-mono tabular-nums">{hoursDisplay}</td>
                          <td className="px-2 py-2 font-mono text-slate-600">{startParts.date}</td>
                          <td className="px-2 py-2 font-mono text-slate-600">{startParts.time}</td>
                          <td className="px-2 py-2 font-mono text-slate-600">{stopParts.date}</td>
                          <td className="px-2 py-2 font-mono text-slate-600">{stopParts.time}</td>
                          <td className="px-2 py-2 font-mono text-slate-600">{row.timeRecordId ?? '—'}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
